import React, { useState } from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { CategoryAccordion } from "component/shared/category-accordion";
import { CodeBlock } from "component/shared/code-block";
import { SettingsSection } from "component/shared/settings-page";
import { RefreshTable } from "component/shared/refresh-table";
import { Column, Sort } from "component/shared/table";
import { Tag } from "component/shared/tag";
import { Page } from "model/page";
import { WebsitePlatformType } from "type/website-platform";
import { EditPageDialog } from "./edit-page-dialog";
import { CategoryGroup, categoryGroups, isRecommended, isStatusPage, pageDescription, pageLabel } from "./page-catalog";
import { styles } from "./style";

const COPY = {
	description: "Set up IDX pages for clean links that are easy to share in emails, campaigns, and more.",
	headScript:
		"Copy and paste this script into the <head> section of your website. It lets IDX content, like listings or search, load on your pages. It must be present on every page where IDX content will load.",
};

// Rows keep catalogue order; nothing in the section sorts.
const NO_SORT: Sort = { id: "", desc: false };

interface OwnProps {
	pages: Page[];
	baseUrl: string;
	platform: WebsitePlatformType;
	/** Persists one page (PUT page); reject to keep the Set Up / Edit modal open with an error. */
	onSavePage: (page: Page) => Promise<void>;
	/**
	 * The site's head script, shown above the pages so it can always be copied again (MAX-4577), or
	 * undefined where the site doesn't need one (a Wix site with the app installed).
	 */
	headScript?: string;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

/**
 * The "IDX Pages" section. Every permitted page sits in its category's accordion, collapsed by
 * default, with a count of pages set up and any recommended page not set up in the header.
 *
 * The status is Enabled or Disabled, read from the saved `enabled` flag. That one flag is all that is
 * stored, so a page that was never set up and one that was turned off read the same.
 */
const Component: React.FC<Props> = ({ classes = {}, pages, baseUrl, platform, onSavePage, headScript }) => {
	// The page whose Set Up / Edit modal is open: the same modal serves both, by whether the page is set up.
	const [editing, setEditing] = useState<Page>();
	const [expanded, setExpanded] = useState<string[]>([]);
	const toggle = (label: string) =>
		setExpanded((current) => (current.includes(label) ? current.filter((l) => l !== label) : [...current, label]));

	const columns: Column<Page>[] = [
		{
			id: "page",
			title: "Page",
			value: (page) => (
				<span className={classes.pageName}>
					{pageLabel(page)}
					{isStatusPage(page) && <Tag label="Required" variant="primary" size="small" />}
					{isRecommended(page) && <Tag label="Recommended" variant="primary" size="small" />}
				</span>
			),
		},
		{
			id: "description",
			title: "Description",
			value: (page) => pageDescription(page),
		},
		{
			id: "status",
			title: "Status",
			value: (page) => {
				// The status page carries every disabled page's links, so it's always in use.
				if (isStatusPage(page) || page.enabled) {
					return <Tag label="Enabled" variant="success" size="large" icon={<Icons.Check size={16} />} />;
				}
				return <Tag label="Disabled" variant="neutral" size="large" />;
			},
		},
		{
			id: "actions",
			title: "Action",
			align: "right",
			value: (page) =>
				isStatusPage(page) || page.enabled ? (
					<Mui.Button
						variant="text"
						color="primary"
						className={classes.action}
						startIcon={<Icons.Edit size={16} />}
						onClick={() => setEditing(page)}
						aria-label={`Edit ${pageLabel(page)}`}
					>
						Edit
					</Mui.Button>
				) : (
					<Mui.Button
						variant="text"
						color="primary"
						className={classes.action}
						onClick={() => setEditing(page)}
						aria-label={`Set up ${pageLabel(page)}`}
					>
						Set Up
					</Mui.Button>
				),
		},
	];

	const summary = (group: CategoryGroup) => (
		<span className={classes.count}>
			{group.active} of {group.total} set up
			{group.recommendedNotSetUp > 0 && (
				<>
					{" • "}
					<Icons.AlertTriangle size={16} className={classes.warningIcon} aria-hidden />
					{group.recommendedNotSetUp} recommended {group.recommendedNotSetUp === 1 ? "page" : "pages"} not set up
				</>
			)}
		</span>
	);

	return (
		<SettingsSection refreshed title="IDX Pages">
			<Mui.Typography className={classes.description}>{COPY.description}</Mui.Typography>
			{headScript && (
				<div className={classes.headScript}>
					<Mui.Typography className={classes.description}>{COPY.headScript}</Mui.Typography>
					<CodeBlock refreshed code={headScript} language="html" />
				</div>
			)}
			<div>
				{categoryGroups(pages).map((group) => {
					const open = expanded.includes(group.label);
					return (
						<CategoryAccordion
							key={group.label}
							className={classes.accordion}
							summaryClassName={classes.summary}
							expanded={open}
							onToggle={() => toggle(group.label)}
							disableDetailsPadding
							summary={
								<div className={classes.header}>
									<span className={classes.title}>
										{open ? (
											<Icons.ChevronUp size={24} className={classes.chevron} />
										) : (
											<Icons.ChevronDown size={24} className={classes.chevron} />
										)}
										{group.label}
									</span>
									{summary(group)}
								</div>
							}
						>
							<div className={classes.details}>
								<RefreshTable
									items={group.pages}
									columns={columns}
									sort={NO_SORT}
									rowKey={(page) => page.type.id}
									paginate={false}
									embedded
								/>
							</div>
						</CategoryAccordion>
					);
				})}
			</div>
			<EditPageDialog
				page={editing}
				baseUrl={baseUrl}
				statusPagePath={pages.find(isStatusPage)?.path || ""}
				platform={platform}
				onSave={onSavePage}
				onClose={() => setEditing(undefined)}
			/>
		</SettingsSection>
	);
};

export const IdxPagesSection = withStyles(Component, styles);
